import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  TextField,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import type { AppError } from '../../api/contract';
import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { DataSourceBadge } from '../../shared/data/DataSourceBadge';
import { PageState } from '../../shared/data/PageState';
import { ServerDataGrid } from '../../shared/data/ServerDataGrid';
import { ListFilterPanel } from '../../shared/filter/ListFilterPanel';
import { PageHeader } from '../../shared/layout/PageHeader';
import { MutationError, toDialogError } from '../partners/PartnerFormDialog';
import { toPageError } from '../partners/PartnerListPage';
import { AdminUserStatusChip } from './AdminUserListPage';
import { PermissionProfileDialog } from './PermissionProfileDialog';
import { adminUsersRepositoryToken, defaultPermissionProfileListQuery } from './repository';
import type { PermissionProfile, PermissionProfileFilters } from './types';

export function PermissionProfilesPage({ dataSource }: { readonly dataSource: DataSource }) {
  const repository = useRepository(adminUsersRepositoryToken);
  const { t } = useTranslation();
  const [query, setQuery] = useState(defaultPermissionProfileListQuery);
  const [refresh, setRefresh] = useState(0);
  const [dialogProfile, setDialogProfile] = useState<PermissionProfile | null | undefined>(
    undefined,
  );
  const [statusProfile, setStatusProfile] = useState<PermissionProfile | null>(null);
  const profiles = useResource(
    (signal) => repository.listProfiles(query, signal),
    [query, refresh, repository],
    (page) => page.items.length === 0,
  );
  const permissions = useResource(
    (signal) => repository.listPermissions(signal),
    [repository],
    () => false,
  );
  const columns = useMemo<readonly GridColDef<PermissionProfile>[]>(
    () => [
      { field: 'name', headerName: t('adminUsers.fields.profile'), flex: 1, minWidth: 190 },
      {
        field: 'status',
        headerName: t('adminUsers.fields.status'),
        minWidth: 120,
        renderCell: ({ row }) => <AdminUserStatusChip status={row.status} />,
      },
      {
        field: 'partnerId',
        headerName: t('adminUsers.fields.partnerId'),
        minWidth: 220,
        sortable: false,
      },
      {
        field: 'agentId',
        headerName: t('adminUsers.fields.agentId'),
        minWidth: 220,
        sortable: false,
      },
      {
        field: 'permissions',
        headerName: t('adminUsers.fields.permissions'),
        minWidth: 260,
        sortable: false,
        valueGetter: (_value, row) => row.permissions.join(', '),
      },
      {
        field: 'assignedUserCount',
        headerName: t('adminUsers.fields.assignedUsers'),
        minWidth: 130,
        sortable: false,
      },
      {
        field: 'actions',
        headerName: t('adminUsers.fields.actions'),
        minWidth: 240,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={1}>
            <Button onClick={() => setDialogProfile(row)} size="small">
              {t('actions.edit')}
            </Button>
            <Button
              color="warning"
              disabled={row.status === 'CLOSED'}
              onClick={() => setStatusProfile(row)}
              size="small"
            >
              {t('adminUsers.actions.changeStatus')}
            </Button>
          </Stack>
        ),
      },
    ],
    [t],
  );
  const error = profiles.error ?? permissions.error;
  const status =
    profiles.status === 'error' || permissions.status === 'error'
      ? 'error'
      : profiles.status === 'loading' || permissions.status === 'loading'
        ? 'loading'
        : profiles.status;
  return (
    <Box sx={{ minWidth: 0 }}>
      <PageHeader
        actions={
          <>
            <DataSourceBadge source={dataSource} />
            <Button onClick={() => setDialogProfile(null)} variant="contained">
              {t('adminUsers.profiles.create')}
            </Button>
            <Button component={Link} to="/admin-users" variant="outlined">
              {t('adminUsers.title')}
            </Button>
          </>
        }
        subtitle={t('adminUsers.profiles.subtitle')}
        title={t('adminUsers.profiles.title')}
      />
      <ProfileFilters
        onApply={(filters) => setQuery((current) => ({ ...current, page: 1, filters }))}
        value={query.filters}
      />
      <PageState
        emptyMessage={t('adminUsers.profiles.empty')}
        error={toPageError(error, t('adminUsers.errors.profiles'))}
        onRetry={() => setRefresh((value) => value + 1)}
        status={status}
      >
        {profiles.data === null || permissions.data === null ? null : (
          <>
            <Box
              aria-label={t('adminUsers.profiles.gridLabel')}
              role="region"
              sx={{ maxWidth: '100%', overflowX: 'auto' }}
              tabIndex={0}
            >
              <ServerDataGrid
                columns={columns}
                disableRowSelectionOnClick
                onQueryChange={(next) => setQuery({ ...next, page: next.page + 1 })}
                page={profiles.data}
                query={{ ...query, page: query.page - 1 }}
                sx={{ minWidth: 1360 }}
              />
            </Box>
            <PermissionCatalog permissions={permissions.data} />
          </>
        )}
      </PageState>
      {dialogProfile === undefined ? null : dialogProfile === null ? (
        <PermissionProfileDialog
          onClose={() => setDialogProfile(undefined)}
          onSaved={() => setRefresh((value) => value + 1)}
          open
          permissions={permissions.data ?? []}
        />
      ) : (
        <PermissionProfileDialog
          onClose={() => setDialogProfile(undefined)}
          onSaved={() => setRefresh((value) => value + 1)}
          open
          permissions={permissions.data ?? []}
          profile={dialogProfile}
        />
      )}
      {statusProfile === null ? null : (
        <ProfileStatusDialog
          onClose={() => setStatusProfile(null)}
          onSaved={() => setRefresh((value) => value + 1)}
          open
          profile={statusProfile}
        />
      )}
    </Box>
  );
}

function PermissionCatalog({
  permissions,
}: {
  readonly permissions: readonly {
    readonly code: string;
    readonly description: string;
    readonly sensitive: boolean;
    readonly delegable: boolean;
  }[];
}) {
  const { t } = useTranslation();
  const domainLabels: Readonly<Record<string, string>> = {
    AUDIT: t('adminUsers.profiles.domains.audit'),
    MEMBER: t('adminUsers.profiles.domains.member'),
    TRANSACTION: t('adminUsers.profiles.domains.transaction'),
    WALLET: t('adminUsers.profiles.domains.wallet'),
  };
  const groups = permissions.reduce<Record<string, typeof permissions>>((result, permission) => {
    const domain = permission.code.split('_', 1)[0] ?? 'OTHER';
    result[domain] = [...(result[domain] ?? []), permission];
    return result;
  }, {});
  return (
    <Paper component="section" sx={{ mt: 2, p: 2 }} variant="outlined">
      <h2>{t('adminUsers.profiles.catalog')}</h2>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ flexWrap: 'wrap' }}>
        {Object.entries(groups).map(([domain, entries]) => (
          <Paper
            aria-label={domainLabel(domain, domainLabels)}
            component="fieldset"
            key={domain}
            sx={{ flex: '1 1 220px', m: 0, p: 2 }}
            variant="outlined"
          >
            <legend>{domainLabel(domain, domainLabels)}</legend>
            {entries.map((permission) => (
              <div key={permission.code}>
                <strong>{permission.code}</strong> — {permission.description}
                {permission.sensitive ? ` · ${t('adminUsers.profiles.sensitive')}` : ''}
                {!permission.delegable ? ` · ${t('adminUsers.profiles.notDelegable')}` : ''}
              </div>
            ))}
          </Paper>
        ))}
      </Stack>
    </Paper>
  );
}

function ProfileFilters({
  onApply,
  value,
}: {
  readonly onApply: (filters: PermissionProfileFilters) => void;
  readonly value: PermissionProfileFilters;
}) {
  const { t } = useTranslation();
  const { handleSubmit, register, reset } = useForm<PermissionProfileFilters>({
    defaultValues: value,
  });
  return (
    <ListFilterPanel
      ariaLabel={t('filterPanel.label', { page: t('adminUsers.profiles.title') })}
      onClear={() => {
        reset(defaultPermissionProfileListQuery.filters);
        onApply(defaultPermissionProfileListQuery.filters);
      }}
      onSubmit={handleSubmit((filters) =>
        onApply({
          ...filters,
          partnerId: filters.partnerId.trim(),
          agentId: filters.agentId.trim(),
          name: filters.name.trim(),
        }),
      )}
      primaryFields={
        <>
          <TextField
            label={t('adminUsers.fields.partnerId')}
            size="small"
            {...register('partnerId')}
          />
          <TextField label={t('adminUsers.fields.agentId')} size="small" {...register('agentId')} />
          <TextField label={t('adminUsers.fields.profile')} size="small" {...register('name')} />
          <TextField
            defaultValue=""
            label={t('adminUsers.fields.status')}
            select
            size="small"
            {...register('status')}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            <MenuItem value="ACTIVE">ACTIVE</MenuItem>
            <MenuItem value="SUSPENDED">SUSPENDED</MenuItem>
            <MenuItem value="CLOSED">CLOSED</MenuItem>
          </TextField>
        </>
      }
    />
  );
}

function ProfileStatusDialog({
  onClose,
  onSaved,
  open,
  profile,
}: {
  readonly onClose: () => void;
  readonly onSaved: () => void;
  readonly open: boolean;
  readonly profile: PermissionProfile;
}) {
  const repository = useRepository(adminUsersRepositoryToken);
  const { t } = useTranslation();
  const guard = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Pick<AppError, 'message' | 'requestId'> | null>(null);
  const {
    formState: { errors },
    handleSubmit,
    register,
  } = useForm<{ readonly reason: string }>({ defaultValues: { reason: '' } });
  const proposed = profile.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
  const close = () => {
    if (!guard.current) onClose();
  };
  const submit = handleSubmit(async ({ reason }) => {
    if (guard.current) return;
    guard.current = true;
    setBusy(true);
    setError(null);
    try {
      await repository.changeProfileStatus(profile.id, { status: proposed, reason: reason.trim() });
      onSaved();
      onClose();
    } catch (cause) {
      setError(toDialogError(cause, t('adminUsers.errors.profileMutation')));
    } finally {
      guard.current = false;
      setBusy(false);
    }
  });
  return (
    <Dialog fullWidth onClose={close} open={open}>
      <DialogTitle>{t('adminUsers.actions.changeStatus')}</DialogTitle>
      <DialogContent>
        <Stack
          component="form"
          id="profile-status-form"
          onSubmit={submit}
          spacing={2}
          sx={{ pt: 1 }}
        >
          {error === null ? null : <MutationError error={error} />}
          <Alert severity="warning">
            {t('adminUsers.profiles.affected', { count: profile.assignedUserCount })}
          </Alert>
          <Alert severity="info">
            {t('adminUsers.profiles.statusImpact', { current: profile.status, next: proposed })}
          </Alert>
          <TextField
            error={!!errors.reason}
            helperText={errors.reason?.message}
            label={t('adminUsers.fields.reason')}
            multiline
            required
            {...register('reason', {
              required: t('adminUsers.validation.required'),
              validate: (value) => value.trim().length > 0 || t('adminUsers.validation.required'),
            })}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={close}>
          {t('actions.cancel')}
        </Button>
        <Button form="profile-status-form" loading={busy} type="submit" variant="contained">
          {t('actions.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function domainLabel(domain: string, labels: Readonly<Record<string, string>>) {
  return labels[domain] ?? domain.charAt(0) + domain.slice(1).toLocaleLowerCase();
}
